import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Building2, User, Mail, Phone, Lock, Check, ArrowRight, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';

const STEPS = [
  { id: 1, name: 'Details', description: 'Organization & Account' },
  { id: 2, name: 'Verify', description: 'Email Verification' },
  { id: 3, name: 'Done', description: 'All Set!' },
];

const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    org_name: '',
    full_name: '',
    email: '',
    phone: '',
    password: '',
    agreed: false,
  });
  
  // Validation state
  const [errors, setErrors] = useState({});
  const [slugPreview, setSlugPreview] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [emailAvailable, setEmailAvailable] = useState(null);
  
  // OTP state
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const otpRefs = useRef([]);
  
  // Success data
  const [registrationResult, setRegistrationResult] = useState(null);

  // Check slug availability with debounce
  useEffect(() => {
    if (form.org_name.length < 2) {
      setSlugPreview('');
      setSlugAvailable(null);
      return;
    }
    
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/auth/register/check-slug/${encodeURIComponent(form.org_name)}`);
        setSlugPreview(res.data.slug || '');
        setSlugAvailable(res.data.available);
      } catch (err) {
        console.error('Slug check error:', err);
        // Don't set to false on network errors - let user proceed
        // Only set to false if we got an actual "not available" response
        if (err.response?.data?.available === false) {
          setSlugAvailable(false);
        } else {
          // Network error or other issue - assume available and let backend validate on submit
          setSlugAvailable(true);
          console.warn('Could not verify slug availability, will check on submit');
        }
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [form.org_name]);

  // Check email availability with debounce
  useEffect(() => {
    if (!form.email || !form.email.includes('@')) {
      setEmailAvailable(null);
      return;
    }
    
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/auth/register/check-email/${encodeURIComponent(form.email)}`);
        setEmailAvailable(res.data.available);
      } catch (err) {
        setEmailAvailable(null);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [form.email]);

  // OTP timer
  useEffect(() => {
    if (otpExpiresIn > 0) {
      const timer = setInterval(() => {
        setOtpExpiresIn(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [otpExpiresIn]);

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!form.org_name || form.org_name.trim().length < 2) {
      newErrors.org_name = 'Organization name must be at least 2 characters';
    }
    // Only block if slug check explicitly returned false (not on API errors)
    if (slugAvailable === false && slugPreview) {
      newErrors.org_name = 'This organization name is not available. Please try a different name.';
    }
    if (!form.full_name || form.full_name.trim().length < 2) {
      newErrors.full_name = 'Full name must be at least 2 characters';
    }
    if (!form.email || !form.email.includes('@')) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (emailAvailable === false) {
      newErrors.email = 'This email is already registered';
    }
    if (!form.phone || form.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!form.password || form.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(form.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/\d/.test(form.password)) {
      newErrors.password = 'Password must contain at least one number';
    }
    if (!form.agreed) {
      newErrors.agreed = 'You must agree to the terms and privacy policy';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitStep1 = async () => {
    if (!validateStep1()) return;
    
    setLoading(true);
    try {
      const res = await api.post('/auth/register/initiate', {
        org_name: form.org_name.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.replace(/\D/g, ''),
        password: form.password,
      });
      
      toast.success('Verification code sent to your email!');
      setOtpExpiresIn(res.data.expires_in || 600);
      setCanResend(false);
      setStep(2);
      
      // Focus first OTP input
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send verification code';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      setOtp(pastedData.split(''));
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/auth/register/verify-otp', {
        email: form.email.trim().toLowerCase(),
        otp_code: otpCode,
      });
      
      // Store tokens
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('refresh_token', res.data.refresh_token);
      
      setRegistrationResult(res.data);
      setStep(3);
      toast.success('Account created successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid verification code';
      toast.error(message);
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    
    setLoading(true);
    try {
      const res = await api.post('/auth/register/resend-otp', {
        email: form.email.trim().toLowerCase(),
      });
      
      toast.success('New verification code sent!');
      setOtp(['', '', '', '', '', '']);
      setOtpExpiresIn(res.data.expires_in || 600);
      setCanResend(false);
      otpRefs.current[0]?.focus();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to resend code';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleGoToDashboard = () => {
    if (registrationResult) {
      // Store tenant info for the new session
      const tenantSlug = registrationResult.tenant?.slug;
      const accessToken = registrationResult.access_token;
      const refreshToken = registrationResult.refresh_token;
      
      if (tenantSlug && accessToken) {
        // Store tokens properly - use same keys as AuthContext
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', refreshToken);  // Match AuthContext key
        localStorage.setItem('tenant_slug', tenantSlug);
        
        // Store user data with all required fields
        if (registrationResult.user) {
          localStorage.setItem('user', JSON.stringify({
            ...registrationResult.user,
            tenant_id: registrationResult.tenant?.tenant_id,
            role: 'AGENCY_ADMIN'  // New tenant admin is always AGENCY_ADMIN
          }));
        }
        
        // Use full URL redirect to ensure clean context load
        // This forces a full page reload with the new tenant context
        const baseUrl = window.location.origin;
        window.location.href = `${baseUrl}/${tenantSlug}/dashboard`;
      } else {
        // Fallback to relative navigation
        navigate(registrationResult.redirect_url || '/');
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header - Shared component with logo from CMS */}
      <PublicHeader />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl bg-white border-slate-200">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl text-[#0a192f]">Create Your Account</CardTitle>
            <CardDescription className="text-[#0a192f]/70">Start your 14-day free trial</CardDescription>
          </CardHeader>

          {/* Progress Steps */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {STEPS.map((s, idx) => (
                <React.Fragment key={s.id}>
                  <div className="flex flex-col items-center">
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                      ${step > s.id ? 'bg-green-500 text-white' : 
                        step === s.id ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white' : 
                        'bg-slate-200 text-[#0a192f]/60'}
                    `}>
                      {step > s.id ? <Check className="w-4 h-4" /> : s.id}
                    </div>
                    <span className={`text-xs mt-1 ${step >= s.id ? 'text-[#0a192f]' : 'text-[#0a192f]/50'}`}>
                      {s.name}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${step > s.id ? 'bg-green-500' : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <CardContent className="pt-4">
            {/* Step 1: Details */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="org_name" className="text-[#0a192f]">Organization Name <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                    <Input
                      id="org_name"
                      value={form.org_name}
                      onChange={(e) => setForm({ ...form, org_name: e.target.value })}
                      placeholder="e.g., Cherry Holidays"
                      className={`pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20 ${errors.org_name ? 'border-red-500' : ''}`}
                      data-testid="register-org-name"
                    />
                  </div>
                  {slugPreview && (
                    <p className={`text-xs ${slugAvailable ? 'text-green-600' : 'text-red-500'}`}>
                      Your URL: ontrof.com/{slugPreview} {slugAvailable ? '✓' : '(not available)'}
                    </p>
                  )}
                  {errors.org_name && <p className="text-xs text-red-500">{errors.org_name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-[#0a192f]">Your Full Name <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                    <Input
                      id="full_name"
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      placeholder="e.g., Rahul Sharma"
                      className={`pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20 ${errors.full_name ? 'border-red-500' : ''}`}
                      data-testid="register-full-name"
                    />
                  </div>
                  {errors.full_name && <p className="text-xs text-red-500">{errors.full_name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#0a192f]">Email Address <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="e.g., rahul@cherryholidays.com"
                      className={`pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20 ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="register-email"
                    />
                    {emailAvailable === true && (
                      <Check className="absolute right-3 top-3 h-4 w-4 text-green-500" />
                    )}
                  </div>
                  {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-[#0a192f]">Phone / WhatsApp <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                    <Input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="e.g., 9876543210"
                      className={`pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20 ${errors.phone ? 'border-red-500' : ''}`}
                      data-testid="register-phone"
                    />
                  </div>
                  {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#0a192f]">Create Password <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="Min 8 chars, 1 uppercase, 1 number"
                      className={`pl-10 pr-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20 ${errors.password ? 'border-red-500' : ''}`}
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-[#0a192f]/50 hover:text-[#0a192f]"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="agreed"
                    checked={form.agreed}
                    onCheckedChange={(checked) => setForm({ ...form, agreed: checked })}
                    className="border-[#0a192f]/30 data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-cyan-500 data-[state=checked]:to-teal-500 mt-0.5"
                    data-testid="register-agree"
                  />
                  <label htmlFor="agreed" className="text-sm text-[#0a192f]/70 leading-tight">
                    I agree to the <a href="/terms" className="text-cyan-600 hover:text-teal-600 font-medium">Terms of Service</a> and{' '}
                    <a href="/privacy" className="text-cyan-600 hover:text-teal-600 font-medium">Privacy Policy</a>
                  </label>
                </div>
                {errors.agreed && <p className="text-xs text-red-500">{errors.agreed}</p>}

                <Button
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25"
                  onClick={handleSubmitStep1}
                  disabled={loading}
                  data-testid="register-continue"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="h-4 w-4 mr-2" />
                  )}
                  Continue to Verification
                </Button>

                <p className="text-center text-sm text-[#0a192f]/70">
                  Already have an account?{' '}
                  <Link to="/login" className="text-cyan-600 hover:text-teal-600 font-medium">Sign In</Link>
                </p>
              </div>
            )}

            {/* Step 2: OTP Verification */}
            {step === 2 && (
              <div className="space-y-6 text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-full flex items-center justify-center border border-cyan-500/30">
                  <Mail className="w-8 h-8 text-cyan-600" />
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-[#0a192f]">Verify Your Email</h3>
                  <p className="text-sm text-[#0a192f]/60 mt-1">
                    We've sent a 6-digit code to<br />
                    <span className="font-medium text-[#0a192f]">{form.email}</span>
                  </p>
                </div>

                <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                  {otp.map((digit, idx) => (
                    <Input
                      key={idx}
                      ref={el => otpRefs.current[idx] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      className="w-12 h-14 text-center text-2xl font-mono bg-white border-[#0a192f]/30 text-[#0a192f] focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                      data-testid={`otp-input-${idx}`}
                    />
                  ))}
                </div>

                {otpExpiresIn > 0 && (
                  <p className="text-sm text-[#0a192f]/60">
                    Code expires in <span className="font-medium text-cyan-600">{formatTime(otpExpiresIn)}</span>
                  </p>
                )}

                <Button
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25"
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.join('').length !== 6}
                  data-testid="verify-otp"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Verify Email
                </Button>

                <div className="text-sm">
                  <span className="text-[#0a192f]/60">Didn't receive the code? </span>
                  <button
                    onClick={handleResendOtp}
                    disabled={!canResend || loading}
                    className={`${canResend ? 'text-cyan-600 hover:text-teal-600 font-medium' : 'text-[#0a192f]/30'}`}
                  >
                    Resend Code
                  </button>
                </div>

                <button
                  onClick={() => setStep(1)}
                  className="text-sm text-[#0a192f]/60 hover:text-[#0a192f] flex items-center justify-center mx-auto"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to Details
                </button>
              </div>
            )}

            {/* Step 3: Success */}
            {step === 3 && registrationResult && (
              <div className="space-y-6 text-center">
                <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center border border-green-500/30">
                  <Check className="w-8 h-8 text-green-500" />
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-[#0a192f]">Welcome to OnTrOf!</h3>
                  <p className="text-sm text-[#0a192f]/60 mt-1">Your account has been created successfully</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 text-left space-y-2 border border-[#0a192f]/10">
                  <div className="flex justify-between">
                    <span className="text-[#0a192f]/60 text-sm">Organization:</span>
                    <span className="font-medium text-sm text-[#0a192f]">{registrationResult.tenant.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#0a192f]/60 text-sm">Your URL:</span>
                    <span className="font-medium text-sm text-cyan-600">{registrationResult.tenant.login_url}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#0a192f]/60 text-sm">Plan:</span>
                    <span className="font-medium text-sm text-[#0a192f]">{registrationResult.tenant.plan}</span>
                  </div>
                </div>

                <Button
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25"
                  onClick={handleGoToDashboard}
                  data-testid="go-to-dashboard"
                >
                  Go to My Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <p className="text-xs text-[#0a192f]/50">
                  Bookmark your login URL: {registrationResult.tenant.login_url}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Footer - Shared component with logo from CMS */}
      <PublicFooter />
    </div>
  );
};

export default Register;
